import { Component, OnInit, OnDestroy, NgZone, ViewChild } from '@angular/core';
import { ReferenceDataService } from '../../services/reference-data/reference-data.service';
import { GroupSearch } from '../../../models/group-search';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '../../services/user.service';
import { CampaignService } from '../../services/campaign/campaign.service';
import { CommonConstant, CampaignInfluencerStatus } from '../../app.constants';
import { CacheService } from '../../services/cache/cache.service';
import { Location } from '@angular/common';
import { Influencer } from '../../../models/influencer';
import {
  EventTrackerService, UserActionType, Page, TargetType,
  ActivePages, ActionDetails, ActionSource
} from '../../services/event-tracker/event-tracker.service';
import { InfluencerGroupsService } from '../influencer-groups/influencer-groups.service';
import { Subscription } from 'rxjs/Subscription';
import { PlaceholderVisibilityService } from '../../services/placeholder-visibility/placeholder-visibility.service';
import { GroupDetailsComponent } from '../group-details/group-details.component';
import { GroupService } from '../../services/group-details/group.service';
import { ModalService } from '../../services/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { InfluencerService } from '../../services/influencer/influencer.service';
import {CampaignInfluencerStatus as CampaignStatus} from '../../app.constants';
import { InfluencerSearch } from '../../../models/influencer-search';
import { AchooPopupComponent } from '../../core/achoo-popup/achoo-popup.component';
import {Observable} from 'rxjs/Observable';
import {EventFilterType, EventType, EventFilter, NewEvent} from '../../../models/user-notification';
import { StringUtils } from '../../utils/string.utils';
import { WebUtils } from '../../utils/web.utils';

@Component({
  selector: 'influencer-list',
  templateUrl: './influencer-list.component.html',
  styleUrls: ['./influencer-list.component.scss']
})
export class InfluencerListComponent extends GroupDetailsComponent implements OnInit, OnDestroy {

  @ViewChild('InfluencerListError')
  public influencerListError: AchooPopupComponent;

  @ViewChild('SearchErrorDialog')
  private _searchErrorDialog: AchooPopupComponent;

  private activeAccount: number;

  public searchCriteria: InfluencerSearch = new InfluencerSearch();

  public campaignList: any[];
  public influencerGroupList: any[];
  public isLoadingData = false;

  private newAddedInfluencerList = [];

  // campaign criteria
  public campaignId: number;
  public campaignName: string;
  public campaignData;
  public numberOfPostChange = 0;
  public campaignTotalPost = 0;
  public isArchivedCampaign = false;
  private periodRange = 7;

  // campaign influencer search supcription
  public campaignInfluencerSubscription: Subscription;
  // influencers are adding to campaign, use to rollback on error
  private pendingInfluencerToAdd;
  public errorMessage: string;

  // search text from Campaign Overview page;
  private _previousSearch: string;

  constructor(groupService: GroupService,
              protected userService: UserService,
              route: ActivatedRoute,
              router: Router,
              referenceDataService: ReferenceDataService,
              location: Location,
              campaignService: CampaignService,
              influencerGroupService: InfluencerGroupsService,
              modalService: ModalService,
              cacheService: CacheService,
              placeholderVisibilityService: PlaceholderVisibilityService,
              ngZone: NgZone,
              eventTracker: EventTrackerService,
              influencerService: InfluencerService,
              translate: TranslateService) {
    super(groupService, userService, route, router, referenceDataService, location, campaignService, influencerGroupService,
          modalService, cacheService, placeholderVisibilityService, ngZone, eventTracker, influencerService, translate);
    }

  ngOnInit() {
    this.companyId = this.userService.getCompanyId();
    this.initializeSearchInfo();
    this.initializeData();
    this.loadGroupList();
    this.loadCampaignList();
    this.setupPlatformSettingSubscription();
  }

  ngOnDestroy() {
    this.cancelPendingRequest();
    this.destroyPlatformSettingSubscription();
  }

  private initializeData() {
    this.route.queryParams.subscribe(params => {
      this.campaignId = params.selectedCampaign ? Number(params.selectedCampaign) : Number(this.route.snapshot.params.id);
      this.loadCampaignData(this.campaignId, this.companyId);
      this.loadInfluencerList(this.searchCriteria, true);
      this.getCampaignInfluencerPostCount(this.campaignId, this.periodRange);
    });
  }

  private initializeSearchInfo() {
    this.route.queryParams.subscribe(params => {
      Object.keys(params).forEach((key) => {
        if (key === 'searchString') {
          this.openSearchBar = true;
        }
      });
      this.searchCriteria = Object.assign({}, this.searchCriteria);

      if (params.search) {
        this.searchCriteria.searchString = params.search;
        this._previousSearch = params.search;
      }

      this.searchCriteria.orderBy = this.searchCriteria.orderBy ? this.searchCriteria.orderBy : CommonConstant.SORT_BY_REACH;
    });
  }

  private loadCampaignData(campaignId: number, companyId: number) {
    // Get campaign details
    this.campaignService.getCampaign(campaignId, companyId).subscribe(data => {
      // process campaign data
      this.campaignData = data.campaign;
      this.campaignName = data.campaign.name;
      this.isArchivedCampaign = data.campaign.status && data.campaign.status === CampaignStatus.ARCHIVED ? true : false;
    }, (error) => {
      if (error.status === 404) {
        this.router.navigate(['campaigns'], {queryParams: {activeAccount: this.companyId}});
      }
    });
  }

  private loadInfluencerList(influencerSearch: InfluencerSearch, isLoadMore: boolean, hasAddedNewInfluencer = false) {
    this.isLoadingData = true;
    // clear previous influencer list if it's not load more
    if (!isLoadMore) {
      this.influencerList = [];
    }
    this.placeholderVisibilityService.show(CommonConstant.INFLUENCER_LIST_PLACEHOLDER);
    const campaignInfluencerObservers = Observable.forkJoin([
      this.campaignService.searchCampaignInfluencers(influencerSearch, this.campaignId),
      this.userService.getNewEventFromLastTime(EventType.NEW_CAMPAIGN_POST,
        this.companyId, this.userService.lastViewedCampaignTime(),
        new EventFilter(EventFilterType.INFLUENCER_ID, this.campaignId))
    ]);
    this.campaignInfluencerSubscription = campaignInfluencerObservers.subscribe(([res, newInflPostEvent]) => {
      this.isLoadingData = false;
      if (hasAddedNewInfluencer) {
        // Update new add influencer
        const influencerArr = res.filter(item => this.newAddedInfluencerList.find(newAddedItem => item.influencerId === newAddedItem.influencer_id));
        const newInfluencerList = this.influencerList.map(influencerListItem => {
          const influencerNeedToUpdate = influencerArr.find(influencerArrItem => influencerArrItem.influencerId === influencerListItem.influencer_id);
          if (influencerNeedToUpdate) {
            return influencerListItem = this.mapInfluencerCampaign(influencerNeedToUpdate, newInflPostEvent);
          } else {
            return influencerListItem;
          }
        });
        this.influencerList = newInfluencerList;
      } else {
        this.influencerList = res.map(item => this.mapInfluencerCampaign(item, newInflPostEvent));
      }
    }, ([e1, e2]) => {
      if (hasAddedNewInfluencer) {
        this.isLoadingData = false;
        const failedInfluncerArr = this.influencerList.map(ifItem => {
          if (ifItem.isNewAdded) {
            ifItem.isFailedUpdate = true;
            return ifItem;
          }
          return ifItem;
        });
        this.influencerList = failedInfluncerArr;
        this.closePopup();
        this.errorMessage = this.translate.instant('campaigns.campaign_influencer_detail.fecth_kpi_fail');
        this.modalService.open(this.influencerListError, () => {
          this.errorMessage = null;
        }, true);
      } else {
        this.handleSearchError(e1);
      }
    });
  }

  private handleSearchError(error: Error): void {
    this.closePopup();
    const isLoadingCase = StringUtils.isUndefinedNullOrEmpty(this.searchCriteria.searchString);

    if (isLoadingCase) {
      WebUtils.reportErrorToSentry('Campaign Details failed to load influencers.', error);
      this.errorMessage = this.translate.instant('campaigns.campaign_influencer_detail.api_load_fail');
      this.modalService.open(this.influencerListError, () => {
        this.errorMessage = null;
      }, true);
      return;
    }

    // search case
    WebUtils.reportErrorToSentry('Campaign Details search failed.', error);
    this.modalService.openInCenter(this._searchErrorDialog);
  }

  public closePopup(): void {
    this.modalService.close();
  }

  public onRetrySeacrh(): void {
    this.closePopup();
    this.searchInfluencerByText(this.searchCriteria.searchString);
  }

  private getCampaignInfluencerPostCount(campaignId, periodRange) {
    this.campaignService.getCampaignInfluencerPostCount(campaignId, periodRange).subscribe(res => {
      if (res) {
        this.campaignTotalPost = res.value;
        this.numberOfPostChange = res.change;
      }
    });
  }

  back() {
    if (!this.isArchivedCampaign) {
      const queryParams = { activeAccount: this.companyId } as any;

      if (!StringUtils.isUndefinedNullOrEmpty(this._previousSearch)) {
        queryParams.search = this._previousSearch;
      }

      return this.router.navigate(['campaigns'], { queryParams });
    }
    return this.location.back();
  }

  mapInfluencerCampaign(influencer: any, newInfluencerPostEvent: NewEvent): Influencer {
    const influencerCamp: Influencer = new Influencer();
    influencerCamp.country = influencer.country;
    influencerCamp.influencer_id = influencer.influencerId;
    influencerCamp.influencer_name = influencer.influencerName;
    influencerCamp.profilePicture = influencer.profileImage;
    influencerCamp.influencer_image = influencer.profileImage;
    influencerCamp.country_code = influencer.country.code;
    influencerCamp.engagement = influencer.engagement;
    influencerCamp.impact = influencer.impact * 100;
    influencerCamp.reach = influencer.reach;
    influencerCamp.numberOfPosts = influencer.totalPost;
    influencerCamp.numberOfFollowers = influencer.follower;
    influencerCamp.platform = influencer.platformIds ? this.convertPlatformIdToName(influencer.platformIds) : null;
    influencerCamp.campaign_influencer_id = influencer.campaignInfluencerId;
    influencerCamp.influencerStatus = influencer.status;
    const newPosts = newInfluencerPostEvent ? newInfluencerPostEvent.events.find(evt => evt.key === String(influencer.influencerId)) : null;
    if (newPosts) {
      influencerCamp.newPosts = newPosts.value;
    }
    return influencerCamp;
  }

  private convertPlatformIdToName(platformIdArray) {
    const platformArr = [];
    if (platformIdArray.length === 0) {
      return;
    }
    this.platformList.map(item => {
      platformIdArray.map(platformId => {
        if (platformId === item.id) {
          platformArr.push(item.name);
        }
      });
    });
    return platformArr.toString();
  }

  advanceSearchInfluencerList(influencerSearch: InfluencerSearch) {
    this.searchCriteria = Object.assign({}, this.searchCriteria, influencerSearch);
    this.searchCriteria.scrollId = undefined;
    this.fetchInfluencerListOfCampaign();
  }

  fetchInfluencerListOfCampaign(isLoadMore = false) {
    // cancel previous request
    this.cancelPendingRequest();
    // perform search
    this.loadInfluencerList(this.searchCriteria, isLoadMore);
  }

  cancelPendingRequest() {
    if (this.campaignInfluencerSubscription) {
      this.campaignInfluencerSubscription.unsubscribe();
    }
    this.placeholderVisibilityService.hideImmediately(CommonConstant.GROUP_DETAILS_PLACEHOLDER);
  }

  openCampaignDetails(campaignId) {
    this.cacheService.disableCacheRemoving();
    return this.router.navigate(['campaigns', campaignId], {
      queryParams: {
        activeAccount: this.activeAccount,
        isCampaignInfluencer: true
      }
    });
  }

  private loadCampaignList() {
    this.campaignService.getCampaignShortListForCompany(this.companyId).subscribe((res: any) => {
      this.campaignList = res.filter(campaignItem => campaignItem.status !== CampaignInfluencerStatus.ARCHIVED).map(
        (campaign) => ({
          id: campaign.id,
          name: campaign.name,
          brief: campaign.brief
        })
      );
    });
  }

  private loadGroupList() {
    this.influencerGroupService.getInfluencerGroupShortList(this.companyId).subscribe((res: any) => {
      this.influencerGroupList = res.map(group => ({
        id: group.id,
        name: group.name,
        description: group.description
      }));
    });
  }

  selectAllInfluencer(e) {
    if (e) {
      this.influencerList.filter(infl => !infl.isPending).forEach((influencer) => {
        influencer.selected = true;
      });
      super.showNotificationSelectAll();
    } else {
      this.influencerList.filter(infl => !infl.isPending).forEach((influencer) => {
        influencer.selected = false;
      });
      super.closeNotificationSelectAll();
    }
  }

  onSelectSingleInfluencer(selectedInfluencer) {
    this.influencerList.forEach((influencer) => {
      if (influencer.influencer_id === selectedInfluencer.influencer_id) {
        influencer.selected = !influencer.selected;
      }
    });
    if (selectedInfluencer.selected === true) {
      this.eventTracker.trackUserAction(UserActionType.ACTION, Page.CAMPAIGN_DETAILS,
        {
          id: selectedInfluencer.influencer_id,
          name: selectedInfluencer.influencer_name,
          type: TargetType.INFLUENCER
        }, {
          action: UserActionType.SELECTION
        }, undefined, null, null);
    }
  }

  addInfluencerToCurrentCampaign(data) {
    this.isLoadingData = true;
    this.pendingInfluencerToAdd = data;
    let filteredNewAcceptInfluencer = [];
    filteredNewAcceptInfluencer = data.filter(item => item.is_accepted);
    this.newAddedInfluencerList = this.campaignService.mapNewAddedInfluencer(data, filteredNewAcceptInfluencer);
    if (filteredNewAcceptInfluencer && filteredNewAcceptInfluencer.length === 0) {
      this.influencerList = this.influencerList.concat(this.newAddedInfluencerList);
      this.isLoadingData = false;
      const newList = this.influencerList.map(item => {
        if (item.isNewAdded) {
          item.isNewAdded = false;
        }
        return item;
      });
      this.influencerList = newList;
    } else {
      this.influencerList = this.influencerList.concat(this.newAddedInfluencerList);
    }
  }

  addInfluencerToCampaignError() {
    const pendingInfluencerIds = this.pendingInfluencerToAdd.map(infl => infl.influencer_id);
    this.influencerList = this.influencerList.filter((infl) =>
      pendingInfluencerIds.indexOf(infl.influencer_id) < 0 && infl.influencerStatus !== CampaignInfluencerStatus.NEW.toLowerCase());
  }

  addInfluencerAsAcceptedSuccess() {
    this.loadInfluencerList(this.searchCriteria, true, true);
  }

  public deleteInfluencerFromBottomBar(evt) {
    this.deleteInfluencers = evt.influencerToDelete;
    const influencerToDeleteIds = this.deleteInfluencers.map(inf => inf.influencer_id);
    this.deleteCampaignInfluencers(influencerToDeleteIds);
  }

  private deleteCampaignInfluencers(influencerToDeleteIds: number[]) {
    // keep removed indexs and remove influencer on UI immediately
    const deletedInfluencerIndexMap = new Map();
    const influencerToDelete = this.influencerList.filter(item => influencerToDeleteIds.find(idItem => idItem === item.influencer_id));
    this.deleteInfluencers.forEach((di) => {
      const removingIndex = this.influencerList.findIndex(inf => inf.influencer_id === di.influencer_id);
      deletedInfluencerIndexMap.set(di.influencer_id, removingIndex);
      this.influencerList.splice(removingIndex, 1);
    });
    this.deleteInfluencerFromCampaign(influencerToDelete, influencerToDeleteIds, deletedInfluencerIndexMap);
  }

  private deleteInfluencerFromCampaign(influencerToDelete, influencerToDeleteIds: number[], deletedInfluencerIndexMap) {
    if (influencerToDelete && influencerToDelete.length > 0) {
      influencerToDelete.map(influencerItemToDelete => {
        this.campaignService.deleteInfluencerFromCampaign(influencerItemToDelete.campaign_influencer_id).subscribe((res: any) => {
          this.modalService.close();
          this.deleteInfluencers = [];
          this.totalInfluencer = this.totalInfluencer - influencerToDeleteIds.length;
          this.closeBottonBar(true);
          this.eventTracker.trackUserAction(UserActionType.ACTION, Page.INFLUENCER_GROUP, {
            id: influencerItemToDelete.influencer_id,
            name: influencerItemToDelete.influencer_name,
            type: TargetType.INFLUENCER_GROUP
          }, {
            action: UserActionType.UPDATE,
            actionDetails: ActionDetails.REMOVED_INFLUENCERS,
            source: ActionSource.BOTTOM_BAR
          }, undefined, {type: ActivePages.CAMPAIGN_DETAILS});
          this.userService.triggerUpdateActivatedInfluencers();
        }, (error) => {
          this.deleteInfluencers.forEach((di) => {
            this.influencerList.splice(deletedInfluencerIndexMap.get(di.id), 0, di);
          });
          // show error message
          this.errorMessage = this.translate.instant('group_details.influencer.delete_influencer_error');
          this.modalService.open(this.influencerListError, () => {
            this.errorMessage = null;
          }, true);
        });
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/main/influencer-list/influencer-list.component.ts